import React from 'react';
import Icon from '../../../components/AppIcon';

const ServiceSummaryCard = ({ formData }) => {
  const getServiceName = (serviceKey) => {
    const serviceMap = {
      'web-development': 'Web Development & Design',
      'mobile-development': 'Mobile App Development',
      'cloud-services': 'Cloud Infrastructure & DevOps',
      'cybersecurity': 'Cybersecurity Solutions',
      'data-analytics': 'Data Analytics & AI',
      'it-consulting': 'IT Consulting & Strategy',
      'managed-services': 'Managed IT Services',
      'digital-transformation': 'Digital Transformation',
      'Other Services': 'Other Services'
    };
    return serviceMap?.[serviceKey] || serviceKey;
  };

  const getAdditionalServiceName = (serviceId) => {
    const serviceMap = {
      'ui-ux-design': 'UI/UX Design & Prototyping',
      'api-integration': 'API Development & Integration',
      'database-optimization': 'Database Design & Optimization',
      'performance-optimization': 'Performance Optimization',
      'security-audit': 'Security Audit & Compliance',
      'staff-augmentation': 'Staff Augmentation',
      'training-support': 'Training & Technical Support',
      'maintenance': 'Ongoing Maintenance & Updates'
    };
    return serviceMap?.[serviceId] || serviceId;
  };

  const getBudgetDisplay = (budget) => {
    const budgetMap = {
      '5k-15k': '$5,000 - $15,000',
      '15k-30k': '$15,000 - $30,000',
      '30k-50k': '$30,000 - $50,000',
      '50k-100k': '$50,000 - $100,000',
      '100k-250k': '$100,000 - $250,000',
      '250k+': '$250,000+',
      'discuss': 'To be discussed'
    };
    return budgetMap?.[budget] || budget;
  };

  const getTimelineDisplay = (timeline) => {
    const timelineMap = {
      'asap': 'ASAP (Rush project)',
      '1-2-months': '1-2 months',
      '3-4-months': '3-4 months',
      '5-6-months': '5-6 months',
      '6-12-months': '6-12 months',
      '12-months+': '12+ months',
      'flexible': 'Flexible timeline'
    };
    return timelineMap?.[timeline] || timeline;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 sticky top-24">
      <div className="flex items-center space-x-2 mb-6">
        <Icon name="FileText" size={20} className="text-accent" />
        <h3 className="text-lg font-semibold text-card-foreground">Application Summary</h3>
      </div>
      <div className="space-y-6">
        {/* Client Information */}
        {(formData?.firstName || formData?.lastName || formData?.email) && (
          <div>
            <h4 className="font-medium text-card-foreground mb-3 flex items-center">
              <Icon name="User" size={16} className="mr-2 text-accent" />
              Client Details
            </h4>
            <div className="space-y-2 text-sm">
              {(formData?.firstName || formData?.lastName) && (
                <p className="text-muted-foreground">
                  <span className="font-medium">Name:</span> {formData?.firstName} {formData?.lastName}
                </p>
              )}
              {formData?.email && (
                <p className="text-muted-foreground">
                  <span className="font-medium">Email:</span> {formData?.email}
                </p>
              )}
              {formData?.companyName && (
                <p className="text-muted-foreground">
                  <span className="font-medium">Company:</span> {formData?.companyName}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Service Selection */}
        {formData?.primaryService && (
          <div>
            <h4 className="font-medium text-card-foreground mb-3 flex items-center">
              <Icon name="Settings" size={16} className="mr-2 text-accent" />
              Services
            </h4>
            <div className="space-y-2">
              <div className="p-3 bg-accent/10 border border-accent/20 rounded-lg">
                <p className="text-sm font-medium text-accent">Primary Service</p>
                <p className="text-sm text-card-foreground mt-1">
                  {getServiceName(formData?.primaryService)}
                </p>
              </div>
              
              {formData?.additionalServices && formData?.additionalServices?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-card-foreground mb-2">Additional Services:</p>
                  <div className="space-y-1">
                    {formData?.additionalServices?.map((service, index) => (
                      <p key={index} className="text-xs text-muted-foreground flex items-center">
                        <Icon name="Check" size={12} className="mr-2 text-accent" />
                        {getAdditionalServiceName(service)}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Project Details */}
        {(formData?.projectTitle || formData?.budget || formData?.timeline) && (
          <div>
            <h4 className="font-medium text-card-foreground mb-3 flex items-center">
              <Icon name="Briefcase" size={16} className="mr-2 text-accent" />
              Project Details
            </h4>
            <div className="space-y-2 text-sm">
              {formData?.projectTitle && (
                <p className="text-muted-foreground">
                  <span className="font-medium">Title:</span> {formData?.projectTitle}
                </p>
              )}
              {formData?.budget && (
                <p className="text-muted-foreground">
                  <span className="font-medium">Budget:</span> {getBudgetDisplay(formData?.budget)}
                </p>
              )}
              {formData?.timeline && (
                <p className="text-muted-foreground">
                  <span className="font-medium">Timeline:</span> {getTimelineDisplay(formData?.timeline)}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Files */}
        {formData?.uploadedFiles && formData?.uploadedFiles?.length > 0 && (
          <div>
            <h4 className="font-medium text-card-foreground mb-3 flex items-center">
              <Icon name="Paperclip" size={16} className="mr-2 text-accent" />
              Attachments
            </h4>
            <p className="text-sm text-muted-foreground">
              {formData?.uploadedFiles?.length} file{formData?.uploadedFiles?.length !== 1 ? 's' : ''} uploaded
            </p>
          </div>
        )}
      </div>
      {/* Estimated Response Time */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="flex items-center space-x-2 mb-2">
          <Icon name="Clock" size={16} className="text-accent" />
          <span className="text-sm font-medium text-card-foreground">Estimated Response</span>
        </div>
        <p className="text-sm text-muted-foreground">
          We'll review your application and respond within 24-48 hours with next steps.
        </p>
      </div>
    </div>
  );
};

export default ServiceSummaryCard;